import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ListChecks, SlidersHorizontal } from "lucide-react";
import * as z from "zod";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { useUpdatePracticeSettings } from "features/practice/hooks/useUpdatePracticeSettings";

import styles from "./PracticeSettingsPage.module.css";
import { buildPracticeSettingsPatch, toPracticeSettingsFormValues } from "./utils/practiceSettingsPatch";

// Consultation lengths that may be added, in minutes.
const CONSULT_TIME_OPTIONS_MINUTES = [5, 10, 15, 30, 45, 60, 75, 90, 105, 120];

const formatMinutesLabel = (minutes) => {
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  return remainder === 0 ? `${hours} h` : `${hours} h ${remainder} min`;
};

const schema = z.object({
  practiceSenderEmailAddress: z.string(),
  canEmailReferrals: z.boolean(),
  allowOverlappingAppointments: z.boolean(),
  allowDeleteAppointments: z.boolean(),
  sendCalendarInvites: z.boolean(),
  showOpenConsultationsOnLogout: z.boolean(),
  healthLink: z.boolean(),
  twoFactorAuthentication: z.boolean(),
  consultMobile: z.boolean(),
  medications: z.boolean(),
  prescriptions: z.boolean(),
  sendEmail: z.boolean(),
  sickCert: z.boolean(),
  letter: z.boolean(),
  consultTypes: z.string().optional(),
  consultTimes: z.string().optional(),
  cancellationReasons: z
    .string()
    .max(10000, { message: "Cancellation reasons exceed 10,000 characters." })
    .optional(),
  consultOutcomes: z
    .string()
    .max(10000, { message: "Consult outcomes exceed 10,000 characters." })
    .optional(),
  reasonForAbsence: z.string().optional(),
  whatCantBeAttended: z.string().optional(),
  userRoles: z.string().optional(),
});

const parseChips = (value) =>
  (value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

const TOGGLE_FIELDS = [
  {
    name: "canEmailReferrals",
    label: "Can Email Referrals",
    description: "Allow sending referrals via email",
  },
  {
    name: "allowOverlappingAppointments",
    label: "Allow Overlapping Appointments",
    description: "Enable booking appointments that overlap with existing ones",
  },
  {
    name: "allowDeleteAppointments",
    label: "Allow Delete Appointments",
    description: "Show the Delete option in the appointment actions menu",
  },
  {
    name: "sendCalendarInvites",
    label: "Send Calendar Invites",
    description: "Automatically send calendar invites for appointments",
  },
  {
    name: "showOpenConsultationsOnLogout",
    label: "Show Open Consultations on Logout",
    description: "Display a warning about open consultations when logging out",
  },
  {
    name: "healthLink",
    label: "Healthlink",
    description: "Enable Healthlink integration for the practice",
  },
  {
    name: "twoFactorAuthentication",
    label: "Two-Factor Authentication",
    description: "Require two-factor authentication for all users in this practice",
  },
  {
    name: "consultMobile",
    label: "Enable mobile consultations",
    description: "Allow clinicians to run consultations through the mobile app",
  },
  {
    name: "medications",
    label: "Medications",
    description:
      "Show the Medications tab on the patient record. Only affects CONSULT-type practices — GP practices always see it.",
  },
  {
    name: "prescriptions",
    label: "Prescriptions",
    description:
      "Show Prescriptions on the patient record and repeat-prescription actions. Only affects CONSULT-type practices — GP practices always see it.",
  },
  {
    name: "sendEmail",
    label: "Send Email",
    description: "Show the Send Email tab on the consult",
  },
  {
    name: "sickCert",
    label: "Sick Cert",
    description: "Show the Add Sick Cert tab on the consult",
  },
  {
    name: "letter",
    label: "Letter",
    description: "Show the Add Letter tab on the consult",
  },
];

const PREFERENCES_TAB = "preferences";
const LISTS_TAB = "lists";

const TAB_OPTIONS = [
  {
    value: PREFERENCES_TAB,
    label: "Preferences",
    icon: <SlidersHorizontal size={16} />,
  },
  { value: LISTS_TAB, label: "Lists", icon: <ListChecks size={16} /> },
];

const LIST_FIELD_NAMES = [
  "consultTypes",
  "consultTimes",
  "cancellationReasons",
  "consultOutcomes",
  "reasonForAbsence",
  "whatCantBeAttended",
  "userRoles",
];

/*
 * Which tab owns each field. The resolver validates the whole form, so a field
 * rejected on the tab that is currently unmounted would make a save look like it
 * did nothing at all — a failed submit uses this to switch to the offending tab.
 */
const FIELD_TABS = [
  ["practiceSenderEmailAddress", PREFERENCES_TAB],
  ...TOGGLE_FIELDS.map(({ name }) => [name, PREFERENCES_TAB]),
  ...LIST_FIELD_NAMES.map((name) => [name, LISTS_TAB]),
];

/*
 * The seven lists differ wildly in height — the outcomes taxonomy carries around
 * thirty chips, "What can't be attended" four — so the two columns are split by
 * heights measured in Chromium rather than by count: two lists on the left and
 * five on the right come out within ~30px of each other, leaving no dead space
 * beside the shorter one. The two consultation lists and the two absence lists
 * each stay side by side.
 */
/*
 * Packed by measured height rather than by reading order: the lists differ enormously
 * (Consultation Outcomes carries ~30 chips, "What can't be attended" four), so pairing
 * them into rows left a tall empty block under whichever column ran out first.
 */
const CHIP_COLUMNS = [
  ["consultOutcomes", "cancellationReasons"],
  [
    "consultTypes",
    "consultTimes",
    "reasonForAbsence",
    "whatCantBeAttended",
    "userRoles",
  ],
];

const SAVE_STATUS_MESSAGES = {
  idle: "Changes save automatically",
  saving: "Saving…",
  error: "Not saved — please try again",
  invalid: "Not saved — check the highlighted field",
};

const SaveStatus = ({ status, savedAtLabel }) => {
  const hasFailed = status === "error" || status === "invalid";
  const message =
    status === "saved"
      ? `Saved ${savedAtLabel}`
      : SAVE_STATUS_MESSAGES[status] ?? SAVE_STATUS_MESSAGES.idle;

  return (
    <span
      className={`${styles.saveStatus} ${hasFailed ? styles.saveStatusError : ""}`}
      role="status"
      aria-live="polite"
      data-testid="practice-settings-save-status"
    >
      {message}
    </span>
  );
};

const ChipListField = ({
  label,
  name,
  chips,
  emptyText,
  registration,
  errorMessage,
  helpText,
  headerAction,
  formatChipLabel = (value) => value,
  onRemoveChip,
  isFilling = false,
}) => (
  <div
    className={`${styles.chipField} ${isFilling ? styles.chipFieldFilling : ""}`}
  >
    {headerAction ? (
      <div className={styles.chipFieldHeader}>
        <label className={styles.fieldLabel} htmlFor={name}>
          {label}
        </label>
        {headerAction}
      </div>
    ) : (
      <label className={styles.fieldLabel} htmlFor={name}>
        {label}
      </label>
    )}

    <div
      className={`${styles.chipBox} ${styles.chipBoxDisabled}`}
      aria-disabled="true"
    >
      {chips.length > 0 ? (
        chips.map((value) => (
          <span key={value} className={styles.chip}>
            {formatChipLabel(value)}
            {!!onRemoveChip && (
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onRemoveChip(value)}
                aria-label={`Remove ${formatChipLabel(value)}`}
              >
                ×
              </button>
            )}
          </span>
        ))
      ) : (
        <span className={styles.chipPlaceholder}>{emptyText}</span>
      )}
    </div>

    <input type="hidden" {...registration} />

    {!!errorMessage && <span className={styles.fieldError}>{errorMessage}</span>}

    {!!helpText && <small className={styles.helpText}>{helpText}</small>}
  </div>
);

export const PracticeSettingsPage = () => {
  const { data: settings, isLoading } = usePracticeSettings();
  const { mutateAsync: updateSettings } = useUpdatePracticeSettings({
    notifyOnSuccess: false,
  });

  const [activeTab, setActiveTab] = useState(PREFERENCES_TAB);
  const [newConsultTime, setNewConsultTime] = useState("");
  const [isAddConsultTimeOpen, setIsAddConsultTimeOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAtLabel, setSavedAtLabel] = useState("");

  const isSavingRef = useRef(false);
  const hasQueuedSaveRef = useRef(false);
  const lastPersistedPayloadRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      practiceSenderEmailAddress: "",
      canEmailReferrals: false,
      allowOverlappingAppointments: false,
      allowDeleteAppointments: false,
      sendCalendarInvites: false,
      showOpenConsultationsOnLogout: true,
      healthLink: false,
      twoFactorAuthentication: true,
      consultMobile: false,
      medications: true,
      prescriptions: true,
      sendEmail: true,
      sickCert: true,
      letter: true,
      consultTypes: "",
      consultTimes: "",
      cancellationReasons: "",
      consultOutcomes: "",
      reasonForAbsence: "",
      whatCantBeAttended: "",
      userRoles: "",
    },
  });

  /*
   * One mapping, shared with the patch builder (KIN-1994). The form used to spell these rules
   * out here and the diff would have had to spell them out again; two copies of "a null
   * boolean means its default" drift the moment a setting is added, and a diff reading the
   * record differently from the form reports changes nobody made.
   */
  useEffect(() => {
    if (settings) {
      reset(toPracticeSettingsFormValues(settings));
    }
  }, [settings, reset]);

  const consultTypeChips = parseChips(watch("consultTypes"));
  const consultTimeChips = parseChips(watch("consultTimes"));
  const cancellationReasonChips = parseChips(watch("cancellationReasons"));
  const consultOutcomeChips = parseChips(watch("consultOutcomes"));
  const reasonForAbsenceChips = parseChips(watch("reasonForAbsence"));
  const whatCantBeAttendedChips = parseChips(watch("whatCantBeAttended"));
  const userRoleChips = parseChips(watch("userRoles"));

  const focusTabWithFirstError = (formErrors) => {
    const firstInvalidField = FIELD_TABS.find(([name]) => !!formErrors[name]);

    if (firstInvalidField) setActiveTab(firstInvalidField[1]);
  };

  const persistSettings = async (data) => {
    const payload = buildPracticeSettingsPatch({ settings, formValues: data });
    const serializedPayload = JSON.stringify(payload);

    // Nothing changed, so there is nothing to patch. The backend would answer such a request
    // with the settings unchanged; not making it at all says the same thing faster.
    if (Object.keys(payload).length === 0) return;

    if (serializedPayload === lastPersistedPayloadRef.current) return;

    setSaveStatus("saving");

    try {
      await updateSettings(payload);
      lastPersistedPayloadRef.current = serializedPayload;
      setSavedAtLabel(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
      setSaveStatus("saved");
    } catch {
      // useUpdatePracticeSettings already reports the reason as an error toast.
      setSaveStatus("error");
    }
  };

  const rejectSave = (formErrors) => {
    setSaveStatus("invalid");
    focusTabWithFirstError(formErrors);
  };

  const submitLatestValues = handleSubmit(persistSettings, rejectSave);

  /*
   * Every request replaces the whole settings object, so two of them in flight at
   * once can land out of order and the older state wins. Only one request runs at
   * a time; a change made while one is in flight queues a single follow-up, and
   * that follow-up re-reads the form on submit, so it always posts the newest
   * state instead of a snapshot taken when the change happened.
   */
  const autoSave = async () => {
    if (isSavingRef.current) {
      hasQueuedSaveRef.current = true;

      return;
    }

    isSavingRef.current = true;

    try {
      await submitLatestValues();
    } finally {
      isSavingRef.current = false;

      if (hasQueuedSaveRef.current) {
        hasQueuedSaveRef.current = false;
        autoSave();
      }
    }
  };

  const addConsultTime = () => {
    const minutes = Number.parseInt(newConsultTime, 10);

    if (!Number.isInteger(minutes) || minutes <= 0) return;
    if (consultTimeChips.includes(String(minutes))) {
      setNewConsultTime("");

      return;
    }

    const next = [...consultTimeChips, String(minutes)]
      .map((value) => Number(value))
      .sort((a, b) => a - b)
      .join(",");

    setValue("consultTimes", next, { shouldDirty: true });
    setNewConsultTime("");
    autoSave();
  };

  const removeConsultTime = (value) => {
    const next = consultTimeChips.filter((entry) => entry !== value).join(",");

    setValue("consultTimes", next, { shouldDirty: true });
    autoSave();
  };

  const availableConsultTimeOptions = CONSULT_TIME_OPTIONS_MINUTES.filter(
    (minutes) => !consultTimeChips.includes(String(minutes))
  ).map((minutes) => ({
    value: String(minutes),
    label: formatMinutesLabel(minutes),
  }));

  const openAddConsultTime = () => {
    setNewConsultTime("");
    setIsAddConsultTimeOpen(true);
  };

  const closeAddConsultTime = () => {
    setNewConsultTime("");
    setIsAddConsultTimeOpen(false);
  };

  const confirmAddConsultTime = () => {
    addConsultTime();
    setIsAddConsultTimeOpen(false);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    autoSave();
  };

  const chipFieldProps = {
    consultTypes: {
      label: "Consultation Types",
      chips: consultTypeChips,
      emptyText: "No consultation types configured",
      registration: register("consultTypes"),
    },
    consultTimes: {
      label: "Consultation Times (minutes)",
      chips: consultTimeChips,
      emptyText: "No consultation times configured",
      registration: register("consultTimes"),
      formatChipLabel: (value) => formatMinutesLabel(Number(value)),
      onRemoveChip: removeConsultTime,
      headerAction: (
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={openAddConsultTime}
        >
          Add
        </Button>
      ),
    },
    cancellationReasons: {
      label: "Cancellation Reasons",
      chips: cancellationReasonChips,
      emptyText: "No cancellation reasons configured",
      registration: register("cancellationReasons"),
      errorMessage: errors.cancellationReasons?.message,
    },
    consultOutcomes: {
      label: "Consultation Outcomes",
      chips: consultOutcomeChips,
      emptyText: "No consultation outcomes configured",
      registration: register("consultOutcomes"),
      errorMessage: errors.consultOutcomes?.message,
    },
    reasonForAbsence: {
      label: "Reason for absence",
      chips: reasonForAbsenceChips,
      emptyText: "No reasons for absence configured",
      registration: register("reasonForAbsence"),
    },
    whatCantBeAttended: {
      label: "What can't be attended",
      chips: whatCantBeAttendedChips,
      emptyText: "No entries configured",
      registration: register("whatCantBeAttended"),
    },
    userRoles: {
      label: "User Roles",
      chips: userRoleChips,
      emptyText: "No user roles configured",
      registration: register("userRoles"),
      helpText:
        'Used to populate the "assign to group" option when creating or editing a task.',
    },
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <>
      <form className={styles.form} onSubmit={handleFormSubmit}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Practice Settings</h3>
          <SaveStatus status={saveStatus} savedAtLabel={savedAtLabel} />
        </div>

        <TabSwitcher
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={setActiveTab}
          fullWidth
        />

        {activeTab === PREFERENCES_TAB && (
          <div className={styles.tabPanel}>
            <div className={styles.emailField}>
              <label
                className={styles.fieldLabel}
                htmlFor="practiceSenderEmailAddress"
              >
                Practice Sender Email Address
              </label>
              <input
                id="practiceSenderEmailAddress"
                type="text"
                className={styles.input}
                placeholder="email@example.com"
                {...register("practiceSenderEmailAddress", {
                  onBlur: autoSave,
                })}
              />

              {!!errors.practiceSenderEmailAddress && (
                <span className={styles.fieldError}>
                  {errors.practiceSenderEmailAddress.message}
                </span>
              )}

              <small className={styles.helpText}>
                The email address used as the sender for practice-related emails
              </small>
            </div>

            <div className={styles.toggleGroup}>
              {TOGGLE_FIELDS.map(({ name, label, description }) => (
                <div key={name} className={styles.toggleItem}>
                  <div className={styles.toggleRow}>
                    <div className="form-check form-switch onoff_area">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={name}
                        {...register(name, { onChange: autoSave })}
                      />
                    </div>
                    <label htmlFor={name} className={styles.toggleLabel}>
                      {label}
                    </label>
                  </div>
                  <small className={styles.helpText}>{description}</small>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === LISTS_TAB && (
          <div className={styles.tabPanel}>
            <div className={styles.chipColumns}>
              {CHIP_COLUMNS.map((columnFieldNames) => (
                <div
                  key={columnFieldNames.join("-")}
                  className={styles.chipColumn}
                >
                  {columnFieldNames.map((fieldName, index) => (
                    <ChipListField
                      key={fieldName}
                      name={fieldName}
                      isFilling={index === columnFieldNames.length - 1}
                      {...chipFieldProps[fieldName]}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </form>

      <Dialog
        open={isAddConsultTimeOpen}
        onClose={closeAddConsultTime}
        title="Add consultation time"
        width={420}
      >
        <div className={styles.dialogBody}>
          <Select
            name="newConsultTime"
            options={availableConsultTimeOptions}
            value={newConsultTime}
            onChange={setNewConsultTime}
            placeholder="Select length"
            fullWidth
          />
          <div className={styles.dialogActions}>
            <Button
              variant="outline"
              size="medium"
              onClick={closeAddConsultTime}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={confirmAddConsultTime}
              disabled={!newConsultTime}
            >
              Add
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
